import "./DatePicker.css";

import { CalendarDate, parseAbsoluteToLocal, parseDate, type ZonedDateTime } from "@internationalized/date";
import { parseISO } from "date-fns";
import { type FC, useMemo } from "react";
import type { DateValue } from "react-aria";
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from "react-aria-components";

import { isDateTimeISOFormat, parseDateToISO } from "../../utils/date/toDateTime.ts";
import { SymbolIcon } from "../Icon/SymbolIcon.js";

export interface DateTimePickerProps<T>
  extends Omit<DatePickerWidgetProps<T>, "onChange" | "value" | "placeholderValue"> {
  ISOFormattedDate: string; // ISO format
  onChange: (ISOFormattedDate?: string | null) => void;
  preventTimezoneConversion?: boolean;
}

export const DateTimePicker: FC<DateTimePickerProps<ZonedDateTime>> = (props) => {
  return (
    <DatePickerWidget
      {...props}
      value={props.ISOFormattedDate ? parseAbsoluteToLocal(props.ISOFormattedDate) : undefined}
      placeholderValue={parseAbsoluteToLocal(new Date().toISOString())}
      disabled={props.disabled}
      onChange={(newDate: DateValue) => {
        const ISOFormattedValue = (newDate as ZonedDateTime)?.toAbsoluteString?.();
        props.onChange?.(ISOFormattedValue);
      }}
    />
  );
};

export const DateOnlyPicker: FC<DateTimePickerProps<CalendarDate>> = (props) => {
  const value = useMemo(() => {
    let value: CalendarDate | undefined;

    if (isDateTimeISOFormat(props.ISOFormattedDate)) {
      if (props.preventTimezoneConversion) {
        const isoDate = parseISO(props.ISOFormattedDate);
        if (isoDate) {
          value = new CalendarDate(
            isoDate.getFullYear(),
            isoDate.getMonth() + 1, // getMonth() returns 0-11, so add 1
            isoDate.getDate(),
          );
        }
      } else {
        const isoDate = parseDateToISO(props.ISOFormattedDate);
        if (isoDate) {
          value = parseDate(isoDate);
        }
      }
    }

    return value;
  }, [props.ISOFormattedDate, props.preventTimezoneConversion]);

  const placeholder = useMemo(() => {
    const isoNow = parseDateToISO(new Date().toISOString());
    return isoNow ? parseDate(isoNow) : undefined;
  }, []);

  return (
    <DatePickerWidget
      {...props}
      value={value}
      placeholderValue={placeholder}
      disabled={props.disabled}
      onChange={(newDate) => {
        const ISOFormattedValue = newDate?.toString();
        props.onChange?.(ISOFormattedValue);
      }}
    />
  );
};

interface DatePickerWidgetProps<T> {
  value: T | undefined | null;
  onChange: (newDate: T) => void;

  inputClassName?: string;
  autoFocus?: boolean;
  placeholderValue?: DateValue;
  defaultOpen?: boolean;
  onClose?: () => void;
  isSelectableDate?: (date: Date) => boolean;
  disabled?: boolean;
}

const DatePickerWidget: FC<DatePickerWidgetProps<DateValue>> = (props) => {
  return (
    <DatePicker
      value={props.value}
      onOpenChange={(isOpen: boolean) => (isOpen ? undefined : props.onClose?.())}
      autoFocus={props.autoFocus}
      onChange={(date) => props.onChange(date as DateValue)}
      // the date provided to this function might be of different types, the callback will be called for every date in the open calendar, but also for the currently selected date !
      isDateUnavailable={
        props.isSelectableDate
          ? (date) =>
              !props.isSelectableDate?.(
                new Date((date as ZonedDateTime)?.toAbsoluteString?.() ?? (date as CalendarDate)?.toString()),
              )
          : undefined
      }
      hideTimeZone={true}
      isDisabled={props.disabled}
      placeholderValue={props.placeholderValue}
      defaultOpen={props.defaultOpen}
    >
      <div className={props.inputClassName}>
        <Group>
          <DateInput>{(segment) => <DateSegment segment={segment} />}</DateInput>
          <Button>
            <SymbolIcon icon="calendar_month" />
          </Button>
        </Group>
      </div>
      {/*
        The minHeight is required to prevent the flip of the component when changing month
        See https://github.com/adobe/react-spectrum/issues/4789
      */}
      <Popover style={{ minHeight: 302 }}>
        <Dialog>
          <Calendar>
            <header>
              <Button slot="previous">
                <SymbolIcon icon="chevron_left" />
              </Button>
              <Heading />
              <Button slot="next">
                <SymbolIcon icon="chevron_right" />
              </Button>
            </header>
            <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  );
};
